import React from 'react';

/**
 * Bulk action bar — appears when a selection exists. Communicates the exact
 * scope (page selection vs. the whole filtered set) so destructive actions are
 * never ambiguous.
 *
 * Props:
 *   count:              number of explicitly selected rows (this page)
 *   totalMatching:      total rows matching the current filters
 *   selectAllMatching:  boolean — the whole filtered set is targeted
 *   pageFullySelected:  boolean — every row on the page is selected (offer select-all-N)
 *   onSelectAllMatching, onClear, onExport, onFlag, onDelete
 *   canDelete:          boolean — render the destructive delete action (default true)
 */
const BulkActionBar = ({
  count = 0, totalMatching = 0, selectAllMatching = false, pageFullySelected = false,
  onSelectAllMatching, onClear, onExport, onDelete, canDelete = true,
}) => {
  if (!selectAllMatching && count === 0) return null;

  return (
    <div className="uv-bulkbar" data-testid="bulk-bar" role="region" aria-label="Bulk actions">
      <b data-testid="bulk-count">
        {selectAllMatching
          ? `All ${totalMatching.toLocaleString()} matching selected`
          : `${count} selected`}
      </b>

      {!selectAllMatching && pageFullySelected && totalMatching > count && (
        <button type="button" className="uv-bulk-link" data-testid="select-all-matching" onClick={onSelectAllMatching}>
          Select all {totalMatching.toLocaleString()} matching
        </button>
      )}

      <span className="uv-bulk-spacer" />

      <button type="button" className="uv-bulk-btn" data-testid="bulk-export" onClick={onExport}>⇩ Export</button>

      {/* Delete acts on explicit rows only (capped + audited via feature 010). */}
      {canDelete && !selectAllMatching && (
        <button type="button" className="uv-bulk-btn uv-bulk-btn--danger" data-testid="bulk-delete" onClick={onDelete}>🗑 Delete</button>
      )}
      {canDelete && selectAllMatching && (
        <span className="uv-bulk-note">Select individual rows to delete.</span>
      )}

      <button type="button" className="uv-bulk-link" data-testid="bulk-clear" onClick={onClear}>Clear</button>
    </div>
  );
};

export default BulkActionBar;
